import {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import debounce from "lodash/debounce";
import produce from "immer";
import { useQueue } from "react-use";
import JourneyFormPager from "./JourneyFormPager";
import JourneyQuestion from "./JourneyQuestion";
import JourneySubmitted from "./JourneySubmitted";
import {
  QuestionAnswer,
  QuestionAnswerType,
  QuestionAnswerValue,
  QuestionTasks,
} from "../../types/forms";
import { JourneyFormDto, JourneyFormGroupDto } from "../../types/dtos/journeys";
import { EnforcedCommentType, FormQuestionDto } from "../../types/dtos/forms";
import TaskManagementConfigDto from "../../types/dtos/forms/TaskManagementConfigDto";
import { JourneyCommentDto } from "../../types/dtos/journeys/JourneyCommentDto";
import {
  buildJourneyFormSteps,
  findFirstFormIndexWithSteps,
  hasEarlierFormWithSteps,
  isLastJourneyFormStep,
  resolveNextJourneyFormStepIndex,
  resolvePreviousJourneyFormStepIndex,
  resolveResumeFormAndStep,
  validateJourneyFormStep,
} from "../../helpers/journeyFormStepHelper";
import {
  CatchUpValidator,
  dateHelper,
  formAnswerHelper,
  goalReviewQuestionHelper,
  interactionHelper,
  journeyAnswerHelper,
  questionTextHelper,
  taskTypeHelper,
} from "../../helpers";
import UserContext from "../../state/UserContext";
import JourneyWelcomeMessage from "./JourneyWelcomeMessage";
import { useAuth } from "react-oidc-context";
import journeyApi from "../../api/forms/journeyApi";
import advancedTasksApi from "../../api/task/advancedTasksApi";
import {
  JourneySubmission,
  JourneySubmissionResponse,
} from "../../types/journeys";
import ResultStatus from "../../types/dtos/generic/ResultStatus";
import { ErrorPopup } from "../common";
import MeetingFormFields from "../../types/catch-ups/MeetingFormFields";
import ManageCatchUpForm from "../catch-ups/ManageCatchUpForm";
import CatchUpDto from "../../types/catch-ups/CatchUpDto";
import catchUpApi from "../../api/dashboard/catchUpApi";
import ContinueJourneyMessage from "./ContinueJourneyMessage";
import {
  JourneyAnswerSetInfo,
  JourneySavedAnswerDto,
  JourneySavedObjectDto,
} from "../../types/dtos/journeys/answers";
import EditableTaskDbObject from "../../types/tasks/EditableTaskDbObject";
import { BaseUserDetailsDto } from "../../types/dtos/generic";
import PreJourneyScreen from "./PreJourneyScreen";
import { t } from "i18next";
import AdvancedTaskDto from "../../types/dtos/forms/AdvancedTaskDto";

interface JourneyFormProps {
  formDetails: JourneyFormGroupDto;
  onFormChange(formDto: JourneyFormDto): void;
  showContinueJourneyMessage: boolean;
  onHideContinueJourneyMessage(): void;
  showWelcomeMessage: boolean;
  onHideWelcomeMessage(): void;
  onLoadNextJourney(): void;
  hasJourneyBeenSubmitted: boolean;
  onJourneySubmissionSuccess(value: boolean): void;
  showManageCatchUpMeetingScreen: boolean;
  onShowManageCatchUpMeetingScreenChange(value: boolean): void;
  formTitle: string | undefined;
  /** Whether or not we're in Dual Prep prep mode */
  isInPrepMode: boolean;
  /** Whether or not the updates are being displayed right now in the journey widget */
  updatesAreVisible: boolean;
  updateCount: number;
  /** True when there's a single update to render, but the assigned journey is for that update */
  onlyUpdateIsForAssignedJourney: boolean;
  onToggleUpdatesVisibility: (showShouldUpdates: boolean) => void;
  createAndPrefillCollabDoc(): void;
  scrollToWidgetTop(): void;
}

function JourneyForm({
  formDetails,
  onFormChange,
  showContinueJourneyMessage,
  onHideContinueJourneyMessage,
  showWelcomeMessage,
  onHideWelcomeMessage,
  onLoadNextJourney,
  hasJourneyBeenSubmitted,
  onJourneySubmissionSuccess,
  showManageCatchUpMeetingScreen,
  onShowManageCatchUpMeetingScreenChange,
  formTitle,
  isInPrepMode,
  updatesAreVisible,
  updateCount,
  onlyUpdateIsForAssignedJourney,
  onToggleUpdatesVisibility,
  createAndPrefillCollabDoc,
  scrollToWidgetTop,
}: JourneyFormProps) {
  // Auth/API
  const auth = useAuth();
  const apiJourneys = new journeyApi(auth.user?.access_token);
  const apiCatchUp = new catchUpApi(auth.user?.access_token);
  const tasksApi = new advancedTasksApi(auth.user?.access_token);
  const answerHelper = new journeyAnswerHelper(apiJourneys);

  // Context
  const userContext = useContext(UserContext);
  const formSubjectUserId = userContext.user.id; // In the future, we might have manager journeys about their people, and this needs to be passed in to validation functions

  // Refs
  const answerSetInfo = useRef<JourneyAnswerSetInfo | null>(null);
  const setAnswerSetInfo = (newInfo: JourneyAnswerSetInfo | null) => {
    answerSetInfo.current = newInfo;
  };

  // State
  const [answerSetParticipants, setAnswerSetParticipants] = useState<
    BaseUserDetailsDto[]
  >([]);
  const [answerState, setAnswerState] = useState<QuestionAnswer[]>([]);
  // The state variable for tasks added in this document, but not yet pushed to the user's dashboard
  const [tasks, setTasks] = useState<QuestionTasks[]>([]);
  const [comments, setComments] = useState<JourneyCommentDto[]>([]);
  const [showValidationErrors, setShowValidationErrors] =
    useState<boolean>(false);
  const [activeFormIndex, setActiveFormIndex] = useState<number>(0);
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  // Local copy of formDetails that we can update directly
  const [localFormDetails, setLocalFormDetails] =
    useState<JourneyFormGroupDto>(formDetails);
  const [enableNextButton, setEnableNextButton] = useState<boolean>(false);
  const [enablePreviousButton, setEnablePreviousButton] =
    useState<boolean>(false);
  const [journeyIsSubmitting, setJourneyIsSubmitting] =
    useState<boolean>(false);
  const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);
  const [errorPopupDetails, setErrorPopupDetails] = useState<any | null>(null);

  const [forceRedirectOnSubmission, setForceRedirectOnSubmission] =
    useState<boolean>(false);
  const [anotherJourneyAvailable, setAnotherJourneyAvailable] =
    useState<boolean>(false);
  const [exitQuestionnaireCompleted, setExitQuestionnaireCompleted] =
    useState<boolean>(false);
  const [dualPrepCollabDocGuidId, setDualPrepCollabDocGuidId] = useState<
    string | null
  >(null);

  const [
    showArrangeMeetingValidationErrors,
    setShowArrangeMeetingValidationErrors,
  ] = useState<boolean>(false);
  const [userSkippedArrangingMeeting, setUserSkippedArrangingMeeting] =
    useState<boolean>(false);
  const [targetDateAndTime, setTargetDateAndTime] = useState<
    Date | undefined
  >();
  const [disableCreateMeetingButton, setDisableCreateMeetingButton] =
    useState<boolean>(false);

  const formFields: MeetingFormFields = {
    subjectUserId: undefined,
    otherParticipantId: undefined,
    subjectClientFormId: undefined,
    targetDate: targetDateAndTime,
    title: formTitle,
  };

  const formSteps = useMemo(
    () =>
      buildJourneyFormSteps(
        localFormDetails.forms[activeFormIndex]?.questions,
      ),
    [localFormDetails, activeFormIndex],
  );
  const activeStep = formSteps[activeStepIndex];

  function getAnswerValueForQuestionId(
    questionId: string,
  ): QuestionAnswerValue | null {
    const match = answerState.find((x) => x.questionId === questionId)?.answer;
    return match ? match : null;
  }

  function getCommentsForQuestionId(questionId: string): JourneyCommentDto[] {
    return comments.filter((x) => x.questionId === questionId);
  }

  function getTasksForQuestionId(questionId: string): QuestionTasks | null {
    return tasks.find((x) => x.questionId === questionId) || null;
  }

  /** Check whether or not the Next button can be enabled for every question on the current step */
  const canEnableNextButton = () => {
    if (!activeStep || activeStep.questions.length === 0) return false;

    return validateJourneyFormStep(
      activeStep,
      getAnswerValueForQuestionId,
      getTasksForQuestionId,
      getCommentsForQuestionId,
      userContext.user.id,
      formSubjectUserId,
      "JOURNEY",
      formDetails.isClientJourneyClosed,
      (config) => {
        if (
          config != null &&
          config.answerSetGuidId == null &&
          answerSetInfo.current?.answerSetUniqueId != null
        ) {
          config.answerSetGuidId = answerSetInfo.current.answerSetUniqueId;
        }
      },
    );
  };

  // Lifecycle events
  useEffect(() => {
    // Select the first form that has a renderable step (skip page-break-only forms)
    const firstFormWithSteps = findFirstFormIndexWithSteps(formDetails.forms);
    setActiveFormIndex(firstFormWithSteps);
    setActiveStepIndex(0);

    setAnswerSetInfo({
      answerSetUniqueId: formDetails.partialSaveData
        ? formDetails.partialSaveData.answerSetUniqueId
        : null,
      answerSetDateCreated: formDetails.partialSaveData
        ? formDetails.partialSaveData.answerSetDateCreated
        : null,
      clientFormVersionIds: formDetails.forms.map((x) => x.clientFormVersionId),
      employeeDetailId: formDetails.employeeDetailId,
      journeyReference: formDetails.journeyReference,
    });

    // If there are saved answers from a previous session answering this journey, load them into state
    if (formDetails.partialSaveData?.answers) {
      setAnswerState(
        formAnswerHelper.prepareAnswersForInitialDisplay(
          formDetails.partialSaveData.answers,
        ),
      );
    }

    if (formDetails.partialSaveData?.comments) {
      setComments(formDetails.partialSaveData?.comments);
    }

    if (formDetails.partialSaveData?.tasks) {
      const questionTasks = taskTypeHelper.getQuestionTaskObjects(
        formDetails.partialSaveData.tasks,
      );
      setTasks(questionTasks);
    }

    // Set the participants
    const participants = [formDetails.subjectUser];
    if (formDetails.managerRolePlayingUser) {
      participants.push(formDetails.managerRolePlayingUser);
    }
    setAnswerSetParticipants(participants);
    // Update local form details when prop changes
    setLocalFormDetails(formDetails);
  }, [formDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // On form change, tell the parent component (so we can change bg colour etc)
    const latestForm = localFormDetails.forms[activeFormIndex];
    onFormChange(latestForm);
  }, [activeFormIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Clamp step index if the form's steps shrink (e.g. after local updates)
    if (formSteps.length > 0 && activeStepIndex >= formSteps.length) {
      setActiveStepIndex(formSteps.length - 1);
    }
    setEnableNextButton(canEnableNextButton());
  }, [activeStepIndex, formSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // When the answer changes, or tasks change for goal setting, conditionally enable the "Next" button
    setEnableNextButton(canEnableNextButton());

    // Show Previous when there is a prior step, or an earlier form with real steps
    // (not merely formIndex > 0, which can be true when leading forms are page-break-only).
    const showPreviousPagerButton: boolean =
      activeStepIndex > 0 ||
      hasEarlierFormWithSteps(localFormDetails.forms, activeFormIndex);
    setEnablePreviousButton(showPreviousPagerButton);
  }, [activeStepIndex, activeFormIndex, answerState, tasks, comments, formSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Ensure rolled over goals are kept in sync for every goal-review question on the step
    if (
      activeStep &&
      activeStep.questions.some((q) => q.isGoalReviewQuestion())
    ) {
      debouncedSyncRolloverGoals(answerState, tasks);
    }
  }, [answerState, activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const onContinueSavedJourney = () => {
    // Find the last question and land on its step (never on a page-break / empty form)
    if (formDetails.partialSaveData?.mostRecentQuestionId) {
      const resume = resolveResumeFormAndStep(
        localFormDetails.forms.length > 0
          ? localFormDetails.forms
          : formDetails.forms,
        formDetails.partialSaveData.mostRecentQuestionId,
      );
      if (resume) {
        setActiveFormIndex(resume.formIndex);
        setActiveStepIndex(resume.stepIndex);
      }
    }

    // Show the normal journey screen
    onHideContinueJourneyMessage();
  };

  const onSnoozeSavedJourney = (
    successCallback: (newExpiryDate: Date) => void,
    errorCallback: () => void,
  ) => {
    apiJourneys.snoozeExpiration(
      answerSetInfo.current!.answerSetUniqueId!,
      successCallback,
      errorCallback,
    );
  };

  /** Update the answer state for the given question with the given value */
  const onValueChange = (
    questionId: string,
    newValue: QuestionAnswerValue,
    answerType: QuestionAnswerType,
    formId: number,
  ) => {
    if (!activeStep?.questions.some((q) => q.questionId === questionId)) {
      return;
    }

    const answerTimestamp = dateHelper.getCurrentDateUtc();

    // Update the existing state answer if there is one, otherwise add it to the state
    // if it's a new answer
    const nextState = produce(answerState, (draft) => {
      const match = draft.find((x) => x.questionId === questionId);
      if (match !== undefined) {
        match.id = null; // TODO: Is this the right thing to do? Or should we remove the answer, and add a new one instead?
        match.answer = newValue;
        match.timestamp = answerTimestamp;
        match.userId = userContext.user.id;
      } else {
        draft.push({
          id: null,
          questionId: questionId,
          answer: newValue,
          timestamp: answerTimestamp,
          userId: userContext.user.id,
          answerType: answerType,
          formId: formId,
        });
      }
    });
    setAnswerState(nextState);
  };

  /** Update the single standard comment for this question */
  const onCommentChange = (
    questionId: string,
    newValue: string | null,
    onSaveSuccess: () => void,
    onSaveError: () => void,
  ) => {
    const stepQuestion = activeStep?.questions.find(
      (q) => q.questionId === questionId,
    );
    // Don't allow the comments to change in journeys if the question isn't on the
    // current step or if the question has comments disabled
    if (!stepQuestion || !stepQuestion.commentsEnabled) return;

    // Update the existing state answer if there is one, otherwise add it to the state
    // if it's a new answer
    const nextState = produce(comments, (draft) => {
      const match = draft.find((x) => x.questionId === questionId);
      if (match !== undefined) {
        // A comment exists in state already, update or remove it, depending
        // on whether or not a value has been supplied
        if (newValue && newValue.trim().length > 0) {
          match.comment = newValue;
        } else {
          const indexToRemove = draft.findIndex(
            (x) => x.questionId === questionId,
          );
          draft.splice(indexToRemove, 1);
        }
      } else if (newValue !== null) {
        draft.push({
          questionId: questionId,
          behaviourId: null,
          goalId: null,
          comment: newValue,
          commentType: "STANDARD",
          clientFormId: null,
        });
      }
    });

    setComments(nextState);

    // The comment state is changed as the popup is closed, so as the user "commits" their change,
    // not as they type each character, so it's safe to perform the API call to save the data here
    answerHelper.saveComment(
      answerSetInfo.current!,
      userContext.user.id,
      questionId,
      newValue,
      "SHARED",
      (saveResponse: JourneySavedObjectDto) => {
        setStateAnswerSetInfoOnAnswerSave(saveResponse);
        onSaveSuccess();
      },
      onSaveError,
    );
  };

  /** Update one of the enforced comments for this question */
  const onEnforcedCommentChange = (
    questionId: string,
    newValue: string | null,
    commentFor: EnforcedCommentType,
    objectId: number,
    clientFormId: number,
  ) => {
    const stepQuestion = activeStep?.questions.find(
      (q) => q.questionId === questionId,
    );
    // Don't allow the comments to change in journeys if the question isn't on the
    // current step or if the question has enforced comments disabled
    if (!stepQuestion || !stepQuestion.hasEnforcedCommentQuestionText()) return;

    // Update the existing state answer if there is one, otherwise add it to the state
    // if it's a new comment
    const nextState = produce(comments, (draft) => {
      const match = draft.find(
        (x) =>
          x.questionId === questionId &&
          x.commentType === "ENFORCED" &&
          ((commentFor === "BEHAVIOUR" && x.behaviourId === objectId) ||
            (commentFor === "GOAL" && x.goalId === objectId)),
      );
      if (match !== undefined) {
        // A comment exists in state already, update or remove it, depending
        // on whether or not a value has been supplied
        if (newValue && newValue.trim().length > 0) {
          match.comment = newValue;
        } else {
          const indexToRemove = draft.findIndex(
            (x) =>
              x.questionId === questionId &&
              ((commentFor === "BEHAVIOUR" && x.behaviourId === objectId) ||
                (commentFor === "GOAL" && x.goalId === objectId)),
          );
          draft.splice(indexToRemove, 1);
        }
      } else if (newValue !== null) {
        draft.push({
          questionId: questionId,
          behaviourId: commentFor === "BEHAVIOUR" ? objectId : null,
          goalId: commentFor === "GOAL" ? objectId : null,
          comment: newValue,
          commentType: "ENFORCED",
          clientFormId: clientFormId,
        });
      }
    });

    setComments(nextState);
  };

  /** When a task is added/edited/deleted */
  const onChangeQuestionTasks = (
    questionTasks: QuestionTasks,
    onSaveSuccess: () => void,
    onSaveError: () => void,
  ) => {
    const onTasksSaveSuccess = (saveResponse: JourneySavedObjectDto) => {
      // Record the AnswerSetUniqueId
      // (only really relevant if this is a new journey, and the first thing to be saved is a task,
      // so we need to store the generated AnswerSetUniqueId to use when saving further answers)
      setAnswerSetInfo({
        ...answerSetInfo.current!,
        answerSetUniqueId: saveResponse.answerSetUniqueId,
        answerSetDateCreated: saveResponse.answerSetDateCreated,
      });

      if (saveResponse.object) {
        // Convert the database tasks into the types expected in the UI
        const dbTasks = saveResponse.object as
          | EditableTaskDbObject<string>[]
          | null;
        const dbTaskCollection = dbTasks
          ? taskTypeHelper.getTaskObjects(dbTasks)
          : [];

        // Change the task state

        const nextState = produce(tasks, (draft) => {
          const match = draft.find(
            (x) => x.questionId === questionTasks.questionId,
          );
          if (match !== undefined) {
            // Update the match what came back from the server
            match.tasks = dbTaskCollection;
          } else {
            const dbQuestionTasks: QuestionTasks = {
              ...questionTasks,
              tasks: dbTaskCollection,
            };
            draft.push(dbQuestionTasks);
          }
        });
        setTasks(nextState);
      }

      onSaveSuccess();
    };

    const onTasksSaveError = () => {
      // Update the state with the submitted values so that we have it stored
      // in the state so we can retry saving the tasks (otherwise they'd be gone!)
      const nextState = produce(tasks, (draft) => {
        const match = draft.find(
          (x) => x.questionId === questionTasks.questionId,
        );
        if (match !== undefined) {
          // Update the match what came back from the server
          match.tasks = questionTasks.tasks;
        } else {
          const dbQuestionTasks: QuestionTasks = {
            ...questionTasks,
            tasks: questionTasks.tasks,
          };
          draft.push(dbQuestionTasks);
        }
      });
      setTasks(nextState);

      onSaveError();
    };

    // Save the tasks via the API
    answerHelper.saveQuestionTasks(
      answerSetInfo.current!,
      questionTasks,
      onTasksSaveSuccess,
      onTasksSaveError,
    );
  };

  const onChangeAdvancedTasks = (
    questionId: string,
    tasks?: AdvancedTaskDto[],
    cancelledTasks?: AdvancedTaskDto[],
    newTasks?: AdvancedTaskDto[],
  ) => {
    // Update localFormDetails directly - this is simpler than caching
    setLocalFormDetails((prev) => {
      const updatedForms = prev.forms.map((form) => {
        const questionIndex = form.questions.findIndex(
          (q) => q.questionId === questionId,
        );

        if (
          questionIndex === -1 ||
          !form.questions[questionIndex].taskManagementConfig
        ) {
          return form;
        }

        const updatedQuestions = [...form.questions];
        const question = { ...updatedQuestions[questionIndex] };
        const taskConfig = { ...question.taskManagementConfig! };

        if (tasks) {
          taskConfig.tasks = tasks;
        }
        if (cancelledTasks) {
          taskConfig.cancelledTasks = cancelledTasks;
        }
        if (newTasks) {
          taskConfig.newTasks = newTasks;
        }

        // Combine the task and newTasks and update the joint list
        taskConfig.liveAndNewTasks = [
          ...taskConfig.tasks,
          ...taskConfig.newTasks,
        ];

        question.taskManagementConfig = taskConfig;
        updatedQuestions[questionIndex] = question;

        return {
          ...form,
          questions: updatedQuestions,
        };
      });

      return {
        ...prev,
        forms: updatedForms,
      };
    });
  };

  const onSwapTask = (
    taskId: string,
    fromQuestionId: string,
    toQuestionId: string,
    isSwappingToNew: boolean,
  ) => {
    // Find the destination question's config to extract values BEFORE async call
    // We need to access the current state, not create FormQuestion objects from proxies
    let toConfig: TaskManagementConfigDto | null = null;

    for (const form of localFormDetails.forms) {
      const toMatch = form.questions.find((q) => q.questionId === toQuestionId);
      if (toMatch?.taskManagementConfig) {
        toConfig = toMatch.taskManagementConfig;
        break;
      }
    }

    if (!toConfig) {
      console.error(
        "Cannot swap task: destination question not found or missing task management config",
      );
      return;
    }

    // Extract primitive values from the destination question's config BEFORE async call
    // This prevents accessing a revoked Immer proxy in the async callback
    const enforceComments = toConfig.enforceComments;
    const subjectEmployeeId = toConfig.subjectEmployeeId;
    const questionMode = toConfig.questionMode;
    const answerSetUniqueId = answerSetInfo.current?.answerSetUniqueId || "";
    const answerSetDateCreated =
      answerSetInfo.current?.answerSetDateCreated || null;

    // Make the API call with the destination question's config
    if (isSwappingToNew) {
      tasksApi.treatTaskAsNewTask(
        taskId,
        answerSetUniqueId,
        enforceComments,
        subjectEmployeeId,
        answerSetDateCreated,
        questionMode,
        (result: ResultStatus) => {
          if (result.success && result.data) {
            // Use the DTO returned from the API
            const updatedTask: AdvancedTaskDto = result.data;

            // Update state with the swapped task
            setLocalFormDetails((prevFormDetails) =>
              produce(prevFormDetails, (draftFormDetails) => {
                let fromQuestion: FormQuestionDto | null = null;
                let toQuestion: FormQuestionDto | null = null;

                for (
                  let iForm = 0;
                  iForm < draftFormDetails.forms.length;
                  iForm++
                ) {
                  const form = draftFormDetails.forms[iForm];
                  const fromMatchIndex = form.questions.findIndex(
                    (q) => q.questionId === fromQuestionId,
                  );
                  const toMatchIndex = form.questions.findIndex(
                    (q) => q.questionId === toQuestionId,
                  );

                  if (fromMatchIndex !== -1) {
                    fromQuestion = form.questions[fromMatchIndex];
                  }
                  if (toMatchIndex !== -1) {
                    toQuestion = form.questions[toMatchIndex];
                  }
                }

                if (
                  !fromQuestion ||
                  !toQuestion ||
                  !fromQuestion.taskManagementConfig ||
                  !toQuestion.taskManagementConfig
                ) {
                  console.error(
                    "Cannot swap task: questions not found or missing task management config",
                  );
                  return;
                }

                const fromConfig = fromQuestion.taskManagementConfig;
                const toConfig = toQuestion.taskManagementConfig;

                // Remove task from source question
                fromConfig.tasks = fromConfig.tasks.filter(
                  (t) => t.taskId !== updatedTask.taskId,
                );
                fromConfig.newTasks = fromConfig.newTasks.filter(
                  (t) => t.taskId !== updatedTask.taskId,
                );
                fromConfig.liveAndNewTasks = [
                  ...fromConfig.tasks,
                  ...fromConfig.newTasks,
                ];

                // Add task to destination question (as new task)
                toConfig.newTasks = [
                  ...toConfig.newTasks.filter(
                    (t) => t.taskId !== updatedTask.taskId,
                  ),
                  updatedTask,
                ];
                toConfig.liveAndNewTasks = [
                  ...toConfig.tasks,
                  ...toConfig.newTasks,
                ];
              }),
            );
          } else {
            console.error("Failed to swap task:", result.message);
          }
        },
        (error: any) => {
          console.error("Error swapping task to setting question:", error);
        },
      );
    } else {
      // Swapping to an existing task
      tasksApi.treatTaskAsExistingTask(
        taskId,
        answerSetUniqueId,
        enforceComments,
        subjectEmployeeId,
        answerSetDateCreated,
        questionMode,
        (result: ResultStatus) => {
          if (result.success && result.data) {
            // Use the DTO returned from the API
            const updatedTask: AdvancedTaskDto = result.data;

            // Update state with the swapped task
            setLocalFormDetails((prevFormDetails) =>
              produce(prevFormDetails, (draftFormDetails) => {
                let fromQuestion: FormQuestionDto | null = null;
                let toQuestion: FormQuestionDto | null = null;

                for (
                  let iForm = 0;
                  iForm < draftFormDetails.forms.length;
                  iForm++
                ) {
                  const form = draftFormDetails.forms[iForm];
                  const fromMatchIndex = form.questions.findIndex(
                    (q) => q.questionId === fromQuestionId,
                  );
                  const toMatchIndex = form.questions.findIndex(
                    (q) => q.questionId === toQuestionId,
                  );

                  if (fromMatchIndex !== -1) {
                    fromQuestion = form.questions[fromMatchIndex];
                  }
                  if (toMatchIndex !== -1) {
                    toQuestion = form.questions[toMatchIndex];
                  }
                }

                if (
                  !fromQuestion ||
                  !toQuestion ||
                  !fromQuestion.taskManagementConfig ||
                  !toQuestion.taskManagementConfig
                ) {
                  console.error(
                    "Cannot swap task: questions not found or missing task management config",
                  );
                  return;
                }

                const fromConfig = fromQuestion.taskManagementConfig;
                const toConfig = toQuestion.taskManagementConfig;

                // Remove task from source question
                fromConfig.tasks = fromConfig.tasks.filter(
                  (t) => t.taskId !== updatedTask.taskId,
                );
                fromConfig.newTasks = fromConfig.newTasks.filter(
                  (t) => t.taskId !== updatedTask.taskId,
                );
                fromConfig.liveAndNewTasks = [
                  ...fromConfig.tasks,
                  ...fromConfig.newTasks,
                ];

                // Add task to destination question (as existing task)
                toConfig.tasks = [
                  ...toConfig.tasks.filter(
                    (t) => t.taskId !== updatedTask.taskId,
                  ),
                  updatedTask,
                ];
                toConfig.liveAndNewTasks = [
                  ...toConfig.tasks,
                  ...toConfig.newTasks,
                ];
              }),
            );
          } else {
            console.error("Failed to swap task:", result.message);
          }
        },
        (error: any) => {
          console.error("Error swapping task to review question:", error);
        },
      );
    }
  };

  const onPreAdvancedTaskInteractionCheckForAnswerSet = (
    onSaveSuccess: (
      answerSetUniqueId: string | null,
      answerSetDateCreated: Date | null,
    ) => void,
    onSaveError: () => void,
  ) => {
    const onTasksSaveSuccess = (response: JourneySavedObjectDto) => {
      // Record the AnswerSetUniqueId
      // (only really relevant if this is a new journey, and the first thing to be saved is a task,
      // so we need to store the generated AnswerSetUniqueId to use when saving further answers)
      setAnswerSetInfo({
        ...answerSetInfo.current!,
        answerSetUniqueId: response.answerSetUniqueId,
        answerSetDateCreated: response.answerSetDateCreated,
      });

      onSaveSuccess(response.answerSetUniqueId, response.answerSetDateCreated);
    };

    // Hit the helper to call the API to get or create the answer set details
    answerHelper.getOrCreateAnswerSet(
      answerSetInfo.current!,
      onTasksSaveSuccess,
      () => {
        onSaveError();
      },
    );
  };

  const onPagerForwards = () => {
    if (!activeStep || activeStep.questions.length === 0) return;

    const questions =
      localFormDetails.forms[activeFormIndex]?.questions ?? [];

    // Validate every question on the current step before advancing
    const stepIsValid = validateJourneyFormStep(
      activeStep,
      getAnswerValueForQuestionId,
      getTasksForQuestionId,
      getCommentsForQuestionId,
      userContext.user.id,
      formSubjectUserId,
      "JOURNEY",
      formDetails.isClientJourneyClosed,
      (config) => {
        if (
          config != null &&
          config.answerSetGuidId == null &&
          answerSetInfo.current?.answerSetUniqueId != null
        ) {
          config.answerSetGuidId = answerSetInfo.current.answerSetUniqueId;
        }
      },
    );

    if (!stepIsValid) {
      setShowValidationErrors(true);
      return;
    }

    setShowValidationErrors(false);

    const sequentialAnswer =
      activeStep.kind === "SEQUENTIAL" && activeStep.questions[0]
        ? getAnswerValueForQuestionId(activeStep.questions[0].questionId)
        : null;

    const nextStepIndex = resolveNextJourneyFormStepIndex(
      formSteps,
      questions,
      activeStepIndex,
      sequentialAnswer,
    );

    if (nextStepIndex != null) {
      setActiveStepIndex(nextStepIndex);
      return;
    }

    // No further step in this form: move to the next form with real steps, or submit
    const isLastForm = activeFormIndex === formDetails.forms.length - 1;
    if (isLastForm) {
      onJourneySubmit();
      return;
    }

    let foundNextForm = false;
    for (
      let iForm = activeFormIndex + 1;
      iForm < formDetails.forms.length;
      iForm++
    ) {
      const nextFormSteps = buildJourneyFormSteps(
        localFormDetails.forms[iForm]?.questions,
      );
      if (nextFormSteps.length > 0) {
        setActiveFormIndex(iForm);
        setActiveStepIndex(0);
        foundNextForm = true;
        break;
      }
    }

    if (!foundNextForm) {
      onJourneySubmit();
    }
  };

  const onPagerBackwards = () => {
    if (!activeStep) return;

    const questions =
      localFormDetails.forms[activeFormIndex]?.questions ?? [];

    const previousStepIndex = resolvePreviousJourneyFormStepIndex(
      formSteps,
      questions,
      activeStepIndex,
      answerState,
    );

    if (previousStepIndex != null) {
      setActiveStepIndex(previousStepIndex);
      return;
    }

    // At the first step of this form: go to the last step of a previous form with real steps
    if (activeFormIndex === 0) {
      return;
    }

    for (let iForm = activeFormIndex - 1; iForm >= 0; iForm--) {
      const previousFormSteps = buildJourneyFormSteps(
        localFormDetails.forms[iForm]?.questions,
      );
      if (previousFormSteps.length > 0) {
        setActiveFormIndex(iForm);
        setActiveStepIndex(previousFormSteps.length - 1);
        return;
      }
    }
  };

  /* **************************************** Answer Saving **************************************** */

  // Use a queue for any API call which will create an AnswerSet if one
  // doesn't already exist, to avoid creating more than one AnswerSet
  // for a journey
  const answerSaveQueue = useQueue<(removeFromQueue: () => void) => void>(); // { add, remove, first, last, size }

  useEffect(() => {
    if (answerSaveQueue.size > 0 && answerSaveQueue.first !== undefined) {
      const apiCall = answerSaveQueue.first;
      apiCall(() => {
        // Ensure this callback happens after `setStateAnswerSetInfoOnAnswerSave` has been called,
        // as that's the whole point of this queue, to avoid two or more API calls creating
        // an AnswerSet each, as the first API call hasn't returned the AnswerSetInfo
        answerSaveQueue.remove();
      });
    }
  }, [answerSaveQueue.first]);

  const setStateAnswerSetInfoOnAnswerSave = (
    saveResponse: JourneySavedAnswerDto | JourneySavedObjectDto,
  ) => {
    setAnswerSetInfo({
      ...answerSetInfo.current!,
      answerSetUniqueId: saveResponse.answerSetUniqueId,
      answerSetDateCreated: saveResponse.answerSetDateCreated,
    });
  };

  const onValueSave = useCallback(
    (answer: QuestionAnswer, onSuccess: () => void, onError: () => void) => {
      if (answer) {
        // Call the API
        answerSaveQueue.add((removeFromQueue: () => void) => {
          answerHelper.saveAnswer(
            answerSetInfo.current!,
            answer.questionId,
            answer,
            (saveResponse: JourneySavedAnswerDto) => {
              setStateAnswerSetInfoOnAnswerSave(saveResponse);
              onSuccess();
              removeFromQueue();
            },
            () => {
              onError();
              removeFromQueue();
            },
          );
        });
      } else {
        onError();
      }
    },
    [
      answerSetInfo.current,
      answerHelper,
      answerSaveQueue,
      setStateAnswerSetInfoOnAnswerSave,
    ],
  );

  const onRetryValueSave = useCallback(
    (questionId: string, onSuccess: () => void, onError: () => void) => {
      let answerToSave: QuestionAnswer | undefined = answerState.find(
        (x) => x.questionId === questionId,
      );

      if (answerToSave) {
        // Call the API
        answerSaveQueue.add((removeFromQueue: () => void) => {
          answerHelper.saveAnswer(
            answerSetInfo.current!,
            questionId,
            answerToSave!,
            (saveResponse: JourneySavedAnswerDto) => {
              setStateAnswerSetInfoOnAnswerSave(saveResponse);
              onSuccess();
              removeFromQueue();
            },
            () => {
              onError();
              removeFromQueue();
            },
          );
        });
      } else {
        console.log(
          "Couldn't find an answer to save for question: " + questionId,
        );
        onError();
      }
    },
    [answerSetInfo.current, answerState, answerHelper, answerSaveQueue],
  );

  const onRetryTasksSave = useCallback(
    (questionId: string, onSuccess: () => void, onError: () => void) => {
      const tasksToSave = tasks.find((x) => x.questionId === questionId);

      if (tasksToSave) {
        // Call the API
        answerSaveQueue.add((removeFromQueue: () => void) => {
          onChangeQuestionTasks(
            tasksToSave,
            () => {
              onSuccess();
              removeFromQueue();
            },
            () => {
              onError();
              removeFromQueue();
            },
          );
        });
      } else {
        console.log(
          "Couldn't find an answer to save for question: " + questionId,
        );
        onError();
      }
    },
    [answerSetInfo.current, tasks, onChangeQuestionTasks, answerSaveQueue],
  );

  const onEnforcedCommentSave = useCallback(
    (
      questionId: string,
      behaviourId: number | null,
      goalId: number | null,
      clientFormId: number,
      onSuccess: () => void,
      onError: () => void,
    ) => {
      const match = comments.find(
        (x) =>
          x.questionId === questionId &&
          x.commentType === "ENFORCED" &&
          (!behaviourId || x.behaviourId === behaviourId) &&
          (!goalId || x.goalId === goalId),
      );

      let commentText: string | null;
      if (
        match !== undefined &&
        match.comment &&
        match.comment.trim().length > 0
      ) {
        // There's an enforced comment with a value to save
        commentText = match.comment.trim();
      } else {
        // Need to delete the enforced comment for this behaviour, if one exists in the db already
        commentText = null;
      }

      // Call the API
      answerSaveQueue.add((removeFromQueue: () => void) => {
        answerHelper.saveEnforcedComment(
          answerSetInfo.current!,
          userContext.user.id,
          questionId,
          behaviourId,
          goalId,
          clientFormId,
          commentText,
          (saveResponse: JourneySavedObjectDto) => {
            setStateAnswerSetInfoOnAnswerSave(saveResponse);
            onSuccess();
            removeFromQueue();
          },
          () => {
            onError();
            removeFromQueue();
          },
        );
      });
    },
    [
      /*docForms,*/ answerSetInfo.current,
      answerState,
      comments,
      answerSaveQueue,
    ],
  );

  const onRetryEnforcedCommentSave = useCallback(
    (
      questionId: string,
      behaviourId: number | null,
      goalId: number | null,
      clientFormId: number,
      onSuccess: () => void,
      onError: () => void,
    ) => {
      // Find the comment to save for this behaviour/goal for this question
      const commentToSave = comments.find(
        (x) =>
          x.questionId === questionId &&
          (!behaviourId || x.behaviourId === behaviourId) &&
          (!goalId || x.goalId === goalId),
      );

      if (commentToSave) {
        // Call the API
        onEnforcedCommentSave(
          questionId,
          behaviourId,
          goalId,
          clientFormId,
          onSuccess,
          onError,
        );
      } else {
        console.log(
          "Couldn't find an enforced comment to save for question: " +
            questionId,
        );
        onError();
      }
    },
    [answerSetInfo.current, comments, onEnforcedCommentSave, answerSaveQueue],
  );

  /* **************************************** End Answer Saving **************************************** */

  /** Call this function when a goal review status radio button is changed
   * to ensure any related goal setting sections are kept in sync
   */
  const synchroniseRolledOverGoals = (
    currentAnswers: QuestionAnswer[],
    currentTasks: QuestionTasks[],
  ): void => {
    if (!activeStep) return;

    const goalReviewQuestions = activeStep.questions.filter(
      (q) => q.isGoalReviewQuestion() && q.goalReviewOptions,
    );
    if (goalReviewQuestions.length === 0) return;

    // Get the goal setting question to rollover to, if there is one
    const goalSettingQuestionId =
      formDetails?.formComplexities?.goalRolloverConfig?.goalSettingQuestionId;

    // If there's no goal setting question, we can't roll over any goals
    if (!goalSettingQuestionId) return;

    for (const goalReviewQuestion of goalReviewQuestions) {
      const checkResult = goalReviewQuestionHelper.checkRolledOverGoals(
        formDetails.formComplexities,
        goalReviewQuestion.goalReviewOptions!,
        currentAnswers,
        currentTasks,
      );

      // Either update an existing answer for the goal setting question, or create one
      let questionTasks =
        currentTasks.find((x) => x.questionId === goalSettingQuestionId)
          ?.tasks || [];

      let performSave = false;
      if (checkResult.goalsToSave) {
        // Save the new rolled over goals to goal setting
        questionTasks = questionTasks.concat(checkResult.goalsToSave);
        performSave = true;
      }

      if (checkResult.taskIdsToDelete) {
        // Mark the relevant goals to be deleted
        questionTasks = questionTasks.map((x) => {
          if (
            x.taskId &&
            checkResult.taskIdsToDelete.indexOf(x.taskId) !== -1
          ) {
            x.modifyStatus = "DELETED";
          }
          return x;
        });

        performSave = true;
      }

      if (performSave) {
        onChangeQuestionTasks(
          {
            formId: goalReviewQuestion.formId,
            questionId: goalSettingQuestionId,
            tasks: questionTasks,
          },
          () => {
            // Tasks saved
          },
          () => {
            // Tasks save error
            console.log("Tasks save errored");
          },
        );
      }
    }
  };

  // use the `debouncedSyncRolloverGoals` function to avoid a race condition
  // caused by quickfire clicking of goal status options to have rolled
  // over goals leftover if status quickly changed from "rollover" to "achieved"
  const debouncedSyncRolloverGoals = useMemo(
    () => debounce(synchroniseRolledOverGoals, 500),
    [activeStep, formDetails, answerSetInfo.current, tasks],
  );

  /** Save the response via the API */
  const onJourneySubmit = () => {
    // Show the loading spinner and disable the submit button on the journey widget
    setJourneyIsSubmitting(true);

    // Don't put the "arrange a catch up" in the user's face on submission,
    // but give them the option to go back and arrange one if they want
    skipArrangeMeeting();

    // Check to see if we need to perform an answer set check (to cover scenarios where a user might
    // not have had to give any answers in the form... e.g. all optional questions, or only one task advanced
    // management question)
    if (formDetails!.shouldPerformAnswerSetCheckOnSubmission) {
      answerHelper.getOrCreateAnswerSet(
        answerSetInfo.current!,
        (response: JourneySavedObjectDto) => {
          const modelToSubmit: JourneySubmission = {
            answerSetUniqueId: response.answerSetUniqueId,
            employeeDetailId: formDetails!.employeeDetailId,
            journeyReference: formDetails!.journeyReference,
          };

          // Need to update answerSetInfo with the new answerSetUniqueId
          setAnswerSetInfo({
            ...answerSetInfo.current!,
            answerSetUniqueId: response.answerSetUniqueId,
            answerSetDateCreated: response.answerSetDateCreated,
          });

          performJourneySubmission(modelToSubmit);
        },
        () => {
          console.log(
            "Error getting/creating new answer set for journey submission",
          );
        },
      );
    } else {
      const modelToSubmit: JourneySubmission = {
        answerSetUniqueId: answerSetInfo.current!.answerSetUniqueId!,
        employeeDetailId: formDetails!.employeeDetailId,
        journeyReference: formDetails!.journeyReference,
      };

      performJourneySubmission(modelToSubmit);
    }
  };

  const performJourneySubmission = (modelToSubmit: JourneySubmission) => {
    apiJourneys.submitResponse(
      modelToSubmit,
      (responseData: JourneySubmissionResponse) => {
        setJourneyIsSubmitting(false);

        if (responseData.successful) {
          onJourneySubmissionSuccess(true);
          setForceRedirectOnSubmission(responseData.forceRedirectToCollabDoc);
          setAnotherJourneyAvailable(responseData.newJourneyAssigned);
          setExitQuestionnaireCompleted(
            responseData.exitQuestionnaireCompleted,
          );
          setDualPrepCollabDocGuidId(responseData.dualPrepCollabDocGuidId);
          setErrorPopupDetails(null);
          interactionHelper.deFocusJourney();
        } else {
          setShowErrorPopup(true);
          setErrorPopupDetails(responseData.error);
        }
      },
      (error) => {
        setJourneyIsSubmitting(false);
        setShowErrorPopup(true);
        setErrorPopupDetails(error);
        console.log("Error saving journey", error);
      },
    );
  };

  if (!activeStep || activeStep.questions.length === 0) return null;

  /** When the user clicks the screen they see alongside the updates to start their current journey */
  const onAdvancePreJourneyScreen = () => {
    onToggleUpdatesVisibility(false);
  };

  /** When the user clicks to view their list of updates again */
  const onBackToUpdatesList = () => {
    onToggleUpdatesVisibility(true);
  };

  // Show the Submit button only on the last form, on the last step
  const showSubmitPagerButton =
    activeFormIndex === formDetails.forms.length - 1 &&
    isLastJourneyFormStep(formSteps, activeStepIndex);

  // Disable the submit button if the last step isn't fully valid
  const disableSubmitPagerButton =
    showSubmitPagerButton && !canEnableNextButton();

  const displayWelcomeMessage =
    !showContinueJourneyMessage &&
    showWelcomeMessage &&
    formDetails.welcomeMessage !== undefined &&
    formDetails.welcomeMessage !== null &&
    formDetails.welcomeMessage.trim().length > 0;

  // Events - Manage Catch Up Screen
  const handleArrangeMeetingFieldChanges = (newValues: MeetingFormFields) => {
    setTargetDateAndTime(newValues.targetDate);
  };

  const checkArrangeMeetingFormIsValid = (): boolean => {
    const validator = new CatchUpValidator(false);

    return validator.isFullyValid(
      {
        subjectUserId: userContext.user.id,
        otherParticipantId: formDetails.managerRolePlayingUser.userId,
        subjectClientFormId: undefined,
        targetDate: targetDateAndTime,
        title: formTitle,
      },
      "JOURNEY-COLLAB-DOC",
    );
  };

  const handleSendCatchUpMeeting = () => {
    // Disable the button to prevent multiple discussions being saved for the same AnswerSet
    setDisableCreateMeetingButton(true);

    setShowArrangeMeetingValidationErrors(false);

    const successCallback = () => {
      // Progress to the final screen by turning off the flag to redirect to manage catch up screen
      onShowManageCatchUpMeetingScreenChange(false);
      setUserSkippedArrangingMeeting(false);
      setDisableCreateMeetingButton(false);
    };

    const errorCallback = () => {
      setDisableCreateMeetingButton(false);
      console.log("Error saving catch up");
    };

    if (checkArrangeMeetingFormIsValid()) {
      const catchUpDetails: CatchUpDto = {
        answerSetUniqueId: answerSetInfo.current!.answerSetUniqueId,
        scheduledDate: targetDateAndTime!,
        scheduledDateLocaleString:
          dateHelper.convertLocaleDateStringForCatchUps(targetDateAndTime!),
        initiatedByEmployeeId: userContext.user.id,
        subjectEmployeeId: userContext.user.id,
        participantEmployeeId: formDetails.managerRolePlayingUser.userId,
        title: formTitle!,
        discussionType: "REVIEW",
      };

      apiCatchUp.CreateNewCatchUpFromJourney(
        catchUpDetails,
        successCallback,
        errorCallback,
      );
    } else {
      setShowArrangeMeetingValidationErrors(true);
      return;
    }
  };

  const skipArrangeMeeting = () => {
    // User has skipped the option to arrange a meeting, therefore enable the skipped flag
    // and relax the flag that displays the meeting screen so the user can see the final screen.
    setUserSkippedArrangingMeeting(true);
    onShowManageCatchUpMeetingScreenChange(false);
  };

  const manageCatchUpFormHeader = (
    <div className="text-white">
      <p>
        {t("Journey.Submit.WhenWouldYouLikeToDiscussThis", {
          managerName: formDetails.managerRolePlayingUser.firstName,
        })}
      </p>
      <br />
    </div>
  );

  const managerCatchUpFormFooter = (
    <div className="flex flex-row-reverse justify-between">
      <div className="flex flex-col justify-end">
        <button
          className="journey-btn-primary disabled:cursor-wait"
          onClick={handleSendCatchUpMeeting}
          disabled={disableCreateMeetingButton}
        >
          {t("Journey.Submit.RequestMeeting")}
        </button>
      </div>
      <button
        className="text-white/50 hover:text-white"
        onClick={skipArrangeMeeting}
      >
        {t("Journey.Submit.DontNeedMeeting")}
      </button>
    </div>
  );

  // Logic for showing the pre journey screen - only when updates are visible and there's no partial journey
  const canShowPreJourneyScreen =
    updatesAreVisible && !formDetails.partialSaveData;

  // Conditionally hide the "Request Catch Up" button based on the module config "HideRequestCatchUpButton"
  const hideRequestCatchUpButton =
    userContext.user.client.moduleConfigs.findIndex(
      (cfg) =>
        cfg.key === "HideRequestCatchUpButton" &&
        cfg.value.trim().toLowerCase() === "true",
    ) >= 0;

  return (
    <>
      <div className="w-full flex flex-col justify-stretch h-full grow">
        {!hasJourneyBeenSubmitted && displayWelcomeMessage && (
          <>
            {canShowPreJourneyScreen && (
              <PreJourneyScreen
                journeyTitle={formTitle!}
                onAdvanceClick={onAdvancePreJourneyScreen}
              />
            )}
            {!canShowPreJourneyScreen && (
              <JourneyWelcomeMessage
                htmlText={questionTextHelper.replaceTokensInFormIntros(
                  formDetails.welcomeMessage!,
                  userContext,
                  formDetails.managerRolePlayingUser,
                )}
                onHide={onHideWelcomeMessage}
                userHasUpdates={
                  updateCount > 0 && !onlyUpdateIsForAssignedJourney
                }
                onBackToUpdates={onBackToUpdatesList}
                isPrefillAnswersEnabled={formDetails.isPrefillAnswersEnabled}
                createAndPrefillCollabDoc={createAndPrefillCollabDoc}
                journeyDeadline={formDetails.journeyDeadline}
              />
            )}
          </>
        )}
        {!hasJourneyBeenSubmitted &&
          showContinueJourneyMessage &&
          formDetails.partialSaveData && (
            <ContinueJourneyMessage
              journeyTitle={formTitle!}
              journeyDeadline={formDetails.journeyDeadline}
              expiryDate={formDetails.partialSaveData.expiryDate}
              onContinue={onContinueSavedJourney}
              onSnooze={onSnoozeSavedJourney}
            />
          )}
        {!hasJourneyBeenSubmitted &&
          !displayWelcomeMessage &&
          !showContinueJourneyMessage && (
            <>
              {activeStep.questions.map((q) => (
                <JourneyQuestion
                  key={q.questionId}
                  answerSetParticipants={answerSetParticipants}
                  question={q}
                  currentValue={getAnswerValueForQuestionId(q.questionId)}
                  onValueChange={(v, t, f) =>
                    onValueChange(q.questionId, v, t, f)
                  }
                  onValueSave={onValueSave}
                  comments={getCommentsForQuestionId(q.questionId)}
                  onCommentChange={(v, s, e) =>
                    onCommentChange(q.questionId, v, s, e)
                  }
                  onEnforcedCommentChange={(v, c, o, f) =>
                    onEnforcedCommentChange(q.questionId, v, c, o, f)
                  }
                  onEnforcedCommentBlur={onEnforcedCommentSave}
                  showValidationErrors={showValidationErrors}
                  enableNextButton={enableNextButton}
                  showSubmitBtn={showSubmitPagerButton}
                  onNextPageClick={onPagerForwards}
                  subjectUser={formDetails.subjectUser}
                  onChangeQuestionTasks={onChangeQuestionTasks}
                  tasks={getTasksForQuestionId(q.questionId)}
                  formComplexities={formDetails.formComplexities}
                  isInPrepMode={isInPrepMode}
                  onRetryValueSave={onRetryValueSave}
                  onRetryTasksSave={onRetryTasksSave}
                  onRetryEnforcedCommentSave={onRetryEnforcedCommentSave}
                  answerSetUniqueId={answerSetInfo.current?.answerSetUniqueId}
                  answerSetDateCreated={
                    answerSetInfo.current?.answerSetDateCreated
                  }
                  isClientJourneyClosed={formDetails.isClientJourneyClosed}
                  advancedTaskTypeSwapabilities={
                    formDetails.advancedTaskTypeSwapabilities
                  }
                  onPreAdvancedTaskInteractionCheckForAnswerSet={
                    onPreAdvancedTaskInteractionCheckForAnswerSet
                  }
                  onChangeAdvancedTasks={onChangeAdvancedTasks}
                  onSwapTask={onSwapTask}
                  disableEnterToAdvance={activeStep.questions.length > 1}
                />
              ))}
              <JourneyFormPager
                enableNextButton={enableNextButton}
                showPrevBtn={enablePreviousButton}
                showSubmitBtn={showSubmitPagerButton}
                disableSubmitBtn={disableSubmitPagerButton}
                onNextPageClick={onPagerForwards}
                onPreviousPageClick={onPagerBackwards}
                onSubmit={onJourneySubmit}
                showSubmitSpinner={journeyIsSubmitting}
                scrollToWidgetTop={scrollToWidgetTop}
              />
            </>
          )}
        {hasJourneyBeenSubmitted &&
          showManageCatchUpMeetingScreen &&
          answerSetInfo.current !== null &&
          !journeyIsSubmitting && (
            <>
              <ManageCatchUpForm
                customHeaderContent={manageCatchUpFormHeader}
                formFieldValues={formFields}
                otherParticipant={formDetails.managerRolePlayingUser}
                onFormChange={handleArrangeMeetingFieldChanges}
                showValidationErrors={showArrangeMeetingValidationErrors}
                answerSetUniqueId={answerSetInfo.current!.answerSetUniqueId}
                customFooterContent={managerCatchUpFormFooter}
                usageScenario="JOURNEY-COLLAB-DOC"
              />
            </>
          )}
        {hasJourneyBeenSubmitted &&
          !showManageCatchUpMeetingScreen &&
          answerSetInfo.current !== null &&
          !journeyIsSubmitting && (
            <JourneySubmitted
              journeyType={formDetails.journeyType}
              subjectUserId={formSubjectUserId}
              answerSetParticipants={answerSetParticipants}
              answerSetUniqueId={answerSetInfo.current!.answerSetUniqueId!}
              forceRedirectToCollabDoc={forceRedirectOnSubmission}
              newJourneyAssigned={anotherJourneyAvailable}
              exitQuestionnaireCompleted={exitQuestionnaireCompleted}
              dualPrepCollabDocGuidId={dualPrepCollabDocGuidId}
              onLoadNextJourney={onLoadNextJourney}
              showRequestCatchUpButton={
                userSkippedArrangingMeeting && !hideRequestCatchUpButton
              }
              onShowManageCatchUpMeetingScreenChange={
                onShowManageCatchUpMeetingScreenChange
              }
            />
          )}
        <ErrorPopup
          error={errorPopupDetails}
          showPopup={showErrorPopup}
          onTogglePopup={setShowErrorPopup}
        />
      </div>
    </>
  );
}

export default JourneyForm;
